import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSun, HiMoon } from 'react-icons/hi';

/**
 * @param {'default' | 'onDark'} [variant]
 * onDark — white/light icon for blue marketing utility bars
 * @param {boolean} [showLabel] — show Light/Dark text next to icon (shell)
 */
const DarkModeToggle = ({ className = '', variant = 'default', showLabel = false }) => {
    const { t } = useTranslation();
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const storedTheme = localStorage.getItem('darkMode');
        if (storedTheme !== null) {
            return storedTheme === 'true';
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    const toggleDarkMode = () => {
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        localStorage.setItem('darkMode', String(newDarkMode));
        document.documentElement.classList.toggle('dark', newDarkMode);
    };

    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDarkMode);
        localStorage.setItem('darkMode', String(isDarkMode));
    }, [isDarkMode]);

    const defaultClass =
        variant === 'onDark'
            ? 'p-2 rounded-lg text-white/90 hover:text-white hover:bg-white/15 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40'
            : 'p-2 rounded-lg text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40';

    const nextModeLabel = isDarkMode
        ? t('shell.chrome.switchToLight', 'Switch to light mode')
        : t('shell.chrome.switchToDark', 'Switch to dark mode');
    const modeLabel = isDarkMode
        ? t('shell.chrome.themeDark', 'Dark')
        : t('shell.chrome.themeLight', 'Light');

    return (
        <button
            type="button"
            className={className || defaultClass}
            onClick={toggleDarkMode}
            aria-label={nextModeLabel}
            aria-pressed={isDarkMode}
            title={nextModeLabel}
        >
            {isDarkMode ? <HiMoon size={showLabel ? 14 : 18} aria-hidden /> : <HiSun size={showLabel ? 14 : 18} aria-hidden />}
            {showLabel ? <span>{modeLabel}</span> : null}
        </button>
    );
};

export default DarkModeToggle;
